export type CaptureErrorKind = 'blocked' | 'missing' | 'busy' | 'unsupported' | 'failed';

export const AUDIO_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
];

export const VIDEO_BITS_PER_SECOND = 2_500_000;

export const AUDIO_BITS_PER_SECOND = 128_000;

export function maxRecordingSeconds(maxSize: number | undefined) {
  if (maxSize === undefined) {
    return;
  }
  return Math.floor((maxSize * 8 * 0.9) / (VIDEO_BITS_PER_SECOND + AUDIO_BITS_PER_SECOND));
}

export const VIDEO_TYPES = [
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

export function pickMimeType(
  candidates: string[],
  isTypeSupported: (type: string) => boolean = (type) => MediaRecorder.isTypeSupported(type),
) {
  return candidates.find((type) => {
    try {
      return isTypeSupported(type);
    } catch {
      return false;
    }
  });
}

export function baseType(mimeType: string) {
  return mimeType.split(';')[0].trim();
}

export function extensionFor(mimeType: string) {
  const type = baseType(mimeType);
  if (type === 'audio/mp4') {
    return 'm4a';
  }
  if (type === 'video/quicktime') {
    return 'mov';
  }
  return type.split('/')[1] || 'bin';
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

export function recordingName(prefix: string, mimeType: string, date = new Date()) {
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}.${pad(date.getMinutes())}.${pad(date.getSeconds())}`;
  return `${prefix} ${day} ${time}.${extensionFor(mimeType)}`;
}

export function formatDuration(seconds: number) {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${pad(whole % 60)}`;
}

export function captureErrorKind(err: unknown): CaptureErrorKind {
  const name = (err as { name?: string } | undefined)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return 'blocked';
  }
  if (
    name === 'NotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'DevicesNotFoundError'
  ) {
    return 'missing';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return 'busy';
  }
  if (name === 'NotSupportedError') {
    return 'unsupported';
  }
  return 'failed';
}

export function canRecord() {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

export function stopStream(stream: MediaStream | undefined) {
  stream?.getTracks().forEach((track) => track.stop());
}

const STOP_TIMEOUT_MS = 10_000;

export type Recording = {
  stop(): Promise<Blob>;
  cancel(): void;
};

export function startRecording(
  stream: MediaStream,
  candidates: string[],
  onInterrupt: () => void = () => {},
  bitrates: { videoBitsPerSecond?: number; audioBitsPerSecond?: number } = {},
): Recording {
  const mimeType = pickMimeType(candidates);
  const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), ...bitrates });
  const chunks: Blob[] = [];
  let isCancelled = false;
  let isStopping = false;
  const stopped = new Promise<Blob>((resolve) => {
    recorder.addEventListener('stop', () => {
      resolve(new Blob(chunks, { type: baseType(recorder.mimeType || mimeType || '') }));
    });
  });

  function interrupted() {
    if (!isCancelled && !isStopping) {
      onInterrupt();
    }
  }
  recorder.addEventListener('dataavailable', (e) => {
    if (e.data.size && !isCancelled) {
      chunks.push(e.data);
    }
  });
  recorder.addEventListener('error', interrupted);
  stream.getTracks().forEach((track) => track.addEventListener('ended', interrupted));
  recorder.start();

  return {
    stop() {
      isStopping = true;
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      return Promise.race([
        stopped,
        new Promise<Blob>((_, reject) =>
          setTimeout(() => reject(new Error('The recorder did not stop')), STOP_TIMEOUT_MS),
        ),
      ]);
    },
    cancel() {
      isCancelled = true;
      chunks.length = 0;
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
    },
  };
}
